/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { ButtonPrimary } from '@/components/button'
import { LoadingScreen } from '@/components/common'
import { MainContent } from '@/components/content'
import { ArrowBack } from '@/components/icons'
import { TextArea } from '@/components/input'
import { MainLayout } from '@/components/layouts'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { CLUB_MODIFY } from '@/constants/Common'
import { EVENT_ANNOUNCEMENT_CREATED } from '@/constants/MessageConstants'
import { ROUTE_CLUBS, ROUTE_EVENTS } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { getClubRoles } from '@/pages/club/clubSlice'
import { clubSelector } from '@/pages/club/selectors'
import { eventSelector } from '@/pages/event/selectors'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { useCreateAnnouncement } from '@/services'
import { PayloadSaveAnnouncement } from '@/types'
import SaveAnnouncementValidationSchema from '@/utils/schema/SaveAnnouncementValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Link, useNavigate, useParams } from 'react-router-dom'

export type CreateNewAnnoucementForm = {
  title: string
  description: string
}

export const CreateNewAnnoucement = () => {
  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)
  const { event } = useAppSelector(eventSelector)

  const { eventId } = params

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const defaultValues: CreateNewAnnoucementForm = {
    title: '',
    description: '',
  }

  const { mutate: createAnnouncement } = useCreateAnnouncement()

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<CreateNewAnnoucementForm>({
    defaultValues,
    resolver: yupResolver(SaveAnnouncementValidationSchema as any),
    mode: 'onTouched',
  })

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true)

    if (eventId && event?.clubId && user) {
      const payload: PayloadSaveAnnouncement = {
        clubId: `${event.clubId}`,
        leagueId: eventId,
        title: data.title,
        description: { content: data.description, contentType: 'text/html' },
      }

      createAnnouncement(payload, {
        onSuccess: () => {
          navigate(`${ROUTE_EVENTS}/${eventId}`)
          toast.success(EVENT_ANNOUNCEMENT_CREATED)
        },
        onError: (errors: any) => {
          toast.error(errors.response.data.message)
        },
      })
    }
  })

  useEffect(() => {
    if (event?.clubId && user?.id) {
      dispatch(getClubRoles({ clubId: event?.clubId, userId: user?.id }))
    }
  }, [event, user])

  useEffect(() => {
    if (permissions && !has(permissions, CLUB_MODIFY))
      navigate(`${ROUTE_CLUBS}/${event?.clubId}`, { relative: 'path', replace: true })
  }, [permissions])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col gap-y-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-x-3 items-center">
                <Link to={`${ROUTE_EVENTS}/${eventId}`} className="hover:opacity-50">
                  <ArrowBack className="text-white" />
                </Link>
                <h1 className="text-sm md:text-xl text-white font-semibold">Create An Annoucement</h1>
              </div>
              <ButtonPrimary title="Save" className="py-3 px-5" onClick={onSubmit} />
            </div>

            <div className={`flex flex-col gap-y-2`}>
              <span className={`text-white text-sm font-semibold`}>Subject</span>
              <textarea
                className="w-full p-2 bg-white rounded"
                rows={3}
                {...register('title')}
                placeholder="Title"
              ></textarea>
              {errors?.title?.message && (
                <div className="text-boldOrange -mt-1 font-medium">{errors?.title?.message}</div>
              )}
            </div>

            <Controller
              name="description"
              control={control}
              render={({ field, formState }) => (
                <TextArea
                  label="Description"
                  placeholder="What are the details?"
                  error={errors?.description?.message}
                  defaultValue={formState.defaultValues?.description}
                  {...field}
                />
              )}
            />
          </div>
        </MainContent>
      </MainLayout>
      {isLoading && <LoadingScreen />}
    </>
  )
}
